import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, ColorPicker, Input, InputNumber, Popover, Select, Tooltip } from 'antd';
import { useAdminSelector } from 'admin/reducers';
import { selectEditableStorySettings } from 'admin/reducers/card-editor/selectors';
import { selectEditableElementId } from 'admin/reducers/card-editor-extra/selectors';
import {
	getCompatibleDesignTokens,
	resolveDesignTokenReference,
	importDesignTokens,
	EMPTY_DESIGN_TOKENS,
	createDesignDefinition,
	flattenDesignTokens,
	type CompiledDesignToken,
	type DesignTokenType,
} from 'common/design-tokens';
import { getBrandKitById } from 'common/brand-kit/projection';
import { SYSTEM_FONTS } from 'common/constants';
import { selectActiveOrganization } from 'admin/reducers/user/selectors';
import { selectEditableStory } from 'admin/reducers/story-editor/selectors';
import { resolveTokenFonts } from 'admin/components/common/AI/Chat/fonts/resolve-token-fonts';
import { registerResolvedFonts } from 'admin/components/pages/Story/StoryEditorChat/tools/update-project/register-resolved-fonts';
import { Icon } from 'admin/components/common/Icon';
import { useQuery } from '@tanstack/react-query';
import { getOrganizationQueryOptions } from 'admin/queries/organization/get-organization';
import { toast } from 'admin/components/common/MessageContainer/toast';
import type { WrappedFieldProps } from 'redux-form';
import t from 'utils/translate';
import type { StorySettingsType } from 'types/story';
import type { InspectorTypes } from '../types';
import { updateStoryTokenValue } from './design-token-value';
import FillPreview from './FillPreview';
import css from './DesignTokenControl.scss';

type Props = WrappedFieldProps & {
	tokenProperty: string;
	literalComponent: React.ElementType;
	forcedValue?: string;
	disabled?: boolean;
	eventListeners?: ReturnType<InspectorTypes.GetFieldListeners>;
};
type TokenSource = 'story' | 'brandKit';
type TokenGroup = { id: string; name: string; tokens: CompiledDesignToken[] };
type EditContext = {
	fieldKey: string;
	tokenId: string;
	tokenType: DesignTokenType;
	settings: StorySettingsType;
};

export default function DesignTokenControl({ tokenProperty, literalComponent: Literal, ...props }: Props) {
	const settings = useAdminSelector(selectEditableStorySettings);
	const tokens = settings.design?.tokens ?? EMPTY_DESIGN_TOKENS;
	const story = useAdminSelector(selectEditableStory);
	const activeOrganization = useAdminSelector(selectActiveOrganization);
	const organizationQuery = useQuery({
		...getOrganizationQueryOptions(story?.organizationId ?? ''),
		enabled: openLibraryEnabled(story?.organizationId, activeOrganization?.id),
	});
	const organization =
		organizationQuery.data ?? (activeOrganization?.id === story?.organizationId ? activeOrganization : undefined);
	const brandKitTokens = getBrandKitById(organization?.metadata?.brandKits, settings.brandKitId)?.design.tokens;
	const latestSettings = useRef(settings);
	latestSettings.current = settings;
	const elementId = useAdminSelector(selectEditableElementId);
	const [open, setOpen] = useState(false);
	const [mode, setMode] = useState<'picker' | 'edit'>('picker');
	const [search, setSearch] = useState('');
	const [draftValue, setDraftValue] = useState<string | number | null>('');
	const [editError, setEditError] = useState('');
	const [busy, setBusy] = useState(false);
	const editContext = useRef<EditContext>();
	const fieldKey = `${story?.id}:${elementId}:${props.input.name}`;
	const current = useRef(fieldKey);
	current.current = fieldKey;
	useEffect(
		() => () => {
			current.current = '';
		},
		[]
	);
	useEffect(() => {
		setOpen(false);
		setMode('picker');
		setSearch('');
		setEditError('');
		editContext.current = undefined;
	}, [fieldKey]);

	const raw = props.input.value || props.forcedValue || '';
	const linked = typeof raw === 'string' && /^var\(\s*--sc-token-/.test(raw);
	const token = typeof raw === 'string' ? resolveDesignTokenReference(raw, tokens) : undefined;
	const missingName = linked
		? raw
				.match(/--sc-token-([a-z0-9-]+)/i)?.[1]
				.replace(/-/g, ' ')
				.replace(/^./, letter => letter.toUpperCase())
		: undefined;
	const label = token
		? `${token.name}: ${token.value}`
		: `${missingName ?? t('designTokens.title')} · ${t('designTokens.unresolved')}`;
	const storyOptions = getCompatibleDesignTokens(tokens, tokenProperty);
	const brandKitOptions = getCompatibleDesignTokens(brandKitTokens, tokenProperty);
	const storyTokenIds = new Set(flattenDesignTokens(tokens).map(item => item.id));
	const availableFontFamilies = useMemo(
		() => [
			...new Set(
				Object.values({ ...SYSTEM_FONTS, ...settings.fonts })
					.filter(Boolean)
					.map(font => font.fontFamily)
			),
		],
		[settings.fonts]
	);
	const query = search.trim().toLowerCase();
	const storyGroups = groupTokens(storyOptions, query);
	const brandKitGroups = groupTokens(brandKitOptions, query);

	const closePanel = () => {
		setOpen(false);
		setMode('picker');
		setSearch('');
		setEditError('');
		editContext.current = undefined;
	};
	const openPicker = () => {
		setMode('picker');
		setEditError('');
		editContext.current = undefined;
		setOpen(true);
	};
	const openEditor = () => {
		if (!token) return;
		editContext.current = {
			fieldKey,
			tokenId: token.id,
			tokenType: token.type,
			settings,
		};
		setDraftValue(token.value);
		setEditError('');
		setMode('edit');
		setOpen(true);
	};
	const apply = async (value: string | number, source: TokenSource = 'story') => {
		setBusy(true);
		try {
			const startSettings = settings;
			let nextTokens = tokens;
			if (source === 'brandKit') {
				const libraryToken =
					typeof value === 'string' ? resolveDesignTokenReference(value, brandKitTokens) : undefined;
				if (!libraryToken || !brandKitTokens) throw new Error('Brand Kit token is unavailable.');
				nextTokens = importDesignTokens(tokens, brandKitTokens, [libraryToken.id]);
			}
			const selected = typeof value === 'string' ? resolveDesignTokenReference(value, nextTokens) : undefined;
			let nextFonts = settings.fonts;
			if (tokenProperty === 'fontFamily') {
				if (!organization || organization.id !== story?.organizationId)
					throw new Error('Story organization is unavailable.');
				const resolved = resolveTokenFonts([String(selected?.value ?? value)], settings.fonts, organization);
				nextFonts = registerResolvedFonts(settings.fonts ?? {}, resolved);
			}
			if (current.current !== fieldKey) return;
			if (latestSettings.current !== startSettings)
				throw new Error('Story settings changed. Choose the token again.');
			if (!props.eventListeners?.onTokenChange) throw new Error('Token binding is unavailable for this field.');
			await props.eventListeners.onTokenChange({
				name: props.input.name,
				value,
				settings: {
					...settings,
					design: { ...(settings.design ?? createDesignDefinition()), tokens: nextTokens },
					fonts: nextFonts,
				},
			});
			props.input.onChange(value);
			closePanel();
		} catch (error) {
			toast.error(error instanceof Error ? error.message : String(error));
		} finally {
			setBusy(false);
		}
	};
	const saveTokenValue = async () => {
		const context = editContext.current;
		if (!context) return;
		setBusy(true);
		setEditError('');
		try {
			if (current.current !== context.fieldKey || latestSettings.current !== context.settings)
				throw new Error('Story or selection changed. Reopen the token editor.');
			const nextTokens = updateStoryTokenValue(
				context.settings.design?.tokens ?? EMPTY_DESIGN_TOKENS,
				context.tokenId,
				context.tokenType,
				draftValue,
				availableFontFamilies
			);
			if (!props.eventListeners?.onTokenValueChange)
				throw new Error('Token value editing is unavailable for this field.');
			await props.eventListeners.onTokenValueChange({
				...context.settings,
				design: { ...(context.settings.design ?? createDesignDefinition()), tokens: nextTokens },
			});
			closePanel();
		} catch (error) {
			setEditError(error instanceof Error ? error.message : String(error));
		} finally {
			setBusy(false);
		}
	};

	const picker = (
		<div className={css.panel} onKeyDown={event => event.key === 'Escape' && closePanel()}>
			<header className={css.pickerHeader}>
				<strong>Connect token</strong>
				<Input
					autoFocus
					allowClear
					aria-label="Search tokens"
					placeholder="Search tokens"
					value={search}
					onChange={event => setSearch(event.target.value)}
				/>
			</header>
			<div className={css.tokenList}>
				<TokenSourceSection
					label="Story"
					source="story"
					groups={storyGroups}
					selectedTokenId={token?.id}
					busy={busy}
					onSelect={apply}
				/>
				<TokenSourceSection
					label="Brand Kit"
					source="brandKit"
					groups={brandKitGroups}
					storyTokenIds={storyTokenIds}
					busy={busy}
					onSelect={apply}
				/>
				{!storyGroups.length && !brandKitGroups.length ? (
					<div className={css.empty}>No matching tokens.</div>
				) : null}
			</div>
			{linked && token ? (
				<footer className={css.pickerFooter}>
					<Button type="text" disabled={busy} onClick={() => apply(token.value)}>
						{t('designTokens.unlink')}
					</Button>
				</footer>
			) : null}
		</div>
	);
	const editorToken = editContext.current
		? flattenDesignTokens(tokens).find(item => item.id === editContext.current?.tokenId)
		: undefined;
	const editor = (
		<div className={css.panel} onKeyDown={event => event.key === 'Escape' && closePanel()}>
			<header className={css.editorHeader}>
				<span>Edit token value</span>
				<strong>{editorToken?.name ?? 'Token unavailable'}</strong>
			</header>
			<div className={css.editorBody}>
				{editorToken ? (
					<TokenValueField
						token={editorToken}
						value={draftValue}
						fontFamilies={availableFontFamilies}
						disabled={busy}
						onChange={value => {
							setDraftValue(value);
							setEditError('');
						}}
					/>
				) : null}
				<p className={css.editorHelp}>Updates every use in this Story.</p>
				{editError ? (
					<p className={css.error} role="alert">
						{editError}
					</p>
				) : null}
			</div>
			<footer className={css.editorFooter}>
				<Button disabled={busy} onClick={closePanel}>
					Cancel
				</Button>
				<Button
					type="primary"
					loading={busy}
					disabled={!editorToken || draftValue === null}
					onClick={saveTokenValue}
				>
					Save
				</Button>
			</footer>
		</div>
	);

	return (
		<div className={css.control}>
			{!linked ? <Literal {...props} /> : null}
			<Popover
				trigger="click"
				open={open}
				onOpenChange={next => (next ? setOpen(true) : closePanel())}
				content={mode === 'edit' ? editor : picker}
				placement="leftTop"
				autoAdjustOverflow
				arrow={false}
				classNames={{ root: css.popover }}
				styles={{ container: { padding: 0 } }}
			>
				<span className={linked ? css.linkedControl : css.triggerAnchor}>
					{linked ? (
						<>
							<button
								type="button"
								className={css.token}
								data-open={open && mode === 'picker'}
								data-unresolved={!token}
								data-token-type={token?.type}
								aria-label={`${t('designTokens.choose')}: ${label}`}
								aria-expanded={open && mode === 'picker'}
								title={label}
								disabled={props.disabled || busy}
								onClick={event => {
									event.stopPropagation();
									openPicker();
								}}
							>
								{token?.type === 'color' ? (
									<FillPreview value={String(token.value)} className={css.colorTokenPreview} />
								) : token?.type === 'fontFamily' ? (
									<span className={css.tokenName}>{token.name}</span>
								) : token ? (
									<>
										<span className={css.tokenValue}>{String(token.value)}</span>
										<span className={css.tokenName}>{token.name}</span>
									</>
								) : (
									<span className={css.tokenLabel}>{label}</span>
								)}
							</button>
							<Tooltip title="Edit token value">
								<button
									type="button"
									className={css.editButton}
									aria-label="Edit token value"
									aria-expanded={open && mode === 'edit'}
									disabled={!token || props.disabled || busy}
									onClick={event => {
										event.stopPropagation();
										openEditor();
									}}
								>
									<Icon type="edit-empty" width={14} />
								</button>
							</Tooltip>
						</>
					) : (
						<button
							type="button"
							className={css.trigger}
							data-open={open}
							aria-label={t('designTokens.choose')}
							aria-expanded={open}
							title={t('designTokens.choose')}
							disabled={props.disabled || busy}
							onClick={event => {
								event.stopPropagation();
								openPicker();
							}}
						>
							<span aria-hidden="true" />
						</button>
					)}
				</span>
			</Popover>
		</div>
	);
}

function TokenSourceSection({
	label,
	source,
	groups,
	selectedTokenId,
	storyTokenIds,
	busy,
	onSelect,
}: {
	label: string;
	source: TokenSource;
	groups: TokenGroup[];
	selectedTokenId?: string;
	storyTokenIds?: Set<string>;
	busy: boolean;
	onSelect: (value: string, source: TokenSource) => void;
}) {
	if (!groups.length) return null;
	return (
		<section className={css.source}>
			<h3>{label}</h3>
			{groups.map(group => (
				<div className={css.group} key={`${source}:${group.id}`}>
					<h4>{group.name}</h4>
					{group.tokens.map(item => {
						const alreadyInStory = source === 'brandKit' && storyTokenIds?.has(item.id);
						const selected = source === 'story' && item.id === selectedTokenId;
						return (
							<button
								type="button"
								className={css.tokenRow}
								data-selected={selected}
								aria-pressed={selected}
								disabled={busy || alreadyInStory}
								onClick={() => onSelect(item.reference, source)}
								key={`${source}:${item.id}`}
							>
								<TokenGlyph token={item} />
								<span className={css.rowName}>{item.name}</span>
								<span className={css.rowValue} title={String(item.value)}>
									{String(item.value)}
								</span>
								{alreadyInStory ? <span className={css.status}>Already in Story</span> : null}
								{selected ? <span className={css.check}>✓</span> : null}
							</button>
						);
					})}
				</div>
			))}
		</section>
	);
}

function TokenGlyph({ token }: { token: CompiledDesignToken }) {
	return token.type === 'color' ? (
		<span className={css.swatch} aria-hidden="true">
			<span style={{ backgroundColor: String(token.value) }} />
		</span>
	) : (
		<span className={css.glyph} aria-hidden="true">
			{{ dimension: '↔', fontFamily: 'Aa', number: '#' }[token.type]}
		</span>
	);
}

function TokenValueField({
	token,
	value,
	fontFamilies,
	disabled,
	onChange,
}: {
	token: CompiledDesignToken;
	value: string | number | null;
	fontFamilies: string[];
	disabled: boolean;
	onChange: (value: string | number | null) => void;
}) {
	if (token.type === 'color')
		return (
			<div className={css.colorEditor}>
				<ColorPicker
					value={String(value ?? '')}
					disabled={disabled}
					onChange={(_, cssValue) => onChange(cssValue)}
					getPopupContainer={trigger => trigger.parentElement ?? document.body}
				/>
				<Input
					aria-label="Color value"
					value={String(value ?? '')}
					disabled={disabled}
					onChange={event => onChange(event.target.value)}
				/>
			</div>
		);
	if (token.type === 'number')
		return (
			<InputNumber
				aria-label="Number value"
				style={{ width: '100%' }}
				value={typeof value === 'number' ? value : null}
				disabled={disabled}
				onChange={onChange}
			/>
		);
	if (token.type === 'fontFamily')
		return (
			<Select
				aria-label="Font family"
				showSearch
				optionFilterProp="label"
				style={{ width: '100%' }}
				value={typeof value === 'string' ? value : undefined}
				disabled={disabled}
				options={fontFamilies.map(family => ({ value: family, label: family }))}
				onChange={onChange}
				getPopupContainer={trigger => trigger.parentElement ?? document.body}
			/>
		);
	return (
		<Input
			aria-label="Size value"
			placeholder="For example 16px or 1rem"
			value={String(value ?? '')}
			disabled={disabled}
			onChange={event => onChange(event.target.value)}
		/>
	);
}

function groupTokens(tokens: CompiledDesignToken[], query: string): TokenGroup[] {
	const groups = new Map<string, TokenGroup>();
	for (const token of tokens) {
		if (
			query &&
			![token.name, token.value, token.collectionName, token.type]
				.map(value => String(value).toLowerCase())
				.some(value => value.includes(query))
		)
			continue;
		const group = groups.get(token.collectionId) ?? {
			id: token.collectionId,
			name: token.collectionName,
			tokens: [],
		};
		group.tokens.push(token);
		groups.set(token.collectionId, group);
	}
	return [...groups.values()];
}

function openLibraryEnabled(storyOrganizationId?: string, activeOrganizationId?: string) {
	return !!storyOrganizationId && storyOrganizationId === activeOrganizationId;
}
